import { apolloClient } from '@/lib/apollo';
import { setToken, setRefreshToken } from '@/utils/tokenManager';
import { AUTHENTICATE_WITH_ACCESS_LINK_TOKEN } from '@/graphql/auth/mutations';
import { removeTokenFromUrl } from '@/utils/urlUtils';

interface ExchangeAccessLinkTokenResponseData {
  accessToken: string;
  refreshToken: string;
  isFirstLogin?: boolean;
  linkToken?: string;
  message?: string;
  version?: string;
  tokenType?: string;
  alreadyVerified?: boolean;
  account?: {
    id: string;
  };
}

/**
 * Authenticates with an access link token
 * @param token The access link token to authenticate with
 * @returns Promise that resolves with the result of the authentication
 */
export const exchangeAccessLinkToken = async (
  token: string
): Promise<{
  success: boolean;
  error?: string;
  data?: ExchangeAccessLinkTokenResponseData;
}> => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: AUTHENTICATE_WITH_ACCESS_LINK_TOKEN,
      variables: { token },
      context: {
        headers: {
          // No authorization header
        },
      },
    });

    // Check if we have a valid response with accessToken
    if (data?.exchangeAccessLinkToken?.accessToken) {
      const { accessToken, refreshToken } = data.exchangeAccessLinkToken;

      // Store the new tokens
      setToken(accessToken);
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }

      // Remove the token from URL to prevent reuse
      removeTokenFromUrl('alt');

      // Store operations handled by calling component
      // Wait a bit to ensure everything is synchronized
      await new Promise(resolve => setTimeout(resolve, 100));

      return {
        success: true,
        data: data.exchangeAccessLinkToken,
      };
    } else {
      removeTokenFromUrl('alt');
      // Return error instead of redirecting
      return {
        success: false,
        error:
          data?.exchangeAccessLinkToken?.message || 'Authentication with access link token failed',
      };
    }
  } catch (error) {
    removeTokenFromUrl('alt');
    console.error('Authentication with access link token failed:', error);
    // Return error instead of redirecting
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Authentication with access link token failed',
    };
  }
};
